import { useState, useEffect } from 'react';
import { oembed } from '@loomhq/loom-embed';

/**
 * Return type of Loom's `oembed` API with the description property added
 * The `description` property is not included as a part of the Loom API's OEmbedInterface (`oembed`'s return type, which is not exported),
 * but on testing it is consistently present in the response.
 * @VisibleForTesting
 */
export type LoomOEmbedResponse = Awaited<ReturnType<typeof oembed>> & {
    description: string;
};

interface UseLoomVideoValidationResult {
    error: string | null; // null = valid/loading, string = error message
    isLoading: boolean;
}

/**
 * Hook to validate Loom video availability using Loom's oembed API
 *
 * Note that the oembed API expects a `share` URL rather than an `embed` URL
 * We currently don't validate this because the URL is validated elsewhere, and any resulting errors are handled gracefully.
 * @example useLoomValidation('https://www.loom.com/share/abc123')
 */
export const useLoomValidation = (
    loomUrl: string
): UseLoomVideoValidationResult => {
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const validateVideo = async () => {
            try {
                if (!loomUrl) {
                    throw new Error('Loom video URL is required');
                }

                const data = (await oembed(loomUrl)) as LoomOEmbedResponse;

                // Check if we got valid oembed data with HTML content
                if (data.html && data.type === 'video') {
                    // Check if the video is private by looking for private video indicators
                    const isPrivate =
                        data.title === 'Private Video' ||
                        data.description === 'This video is private.';

                    if (isPrivate) {
                        setError('Loom video is private and not accessible');
                    }
                    // Video is valid, error remains null
                } else {
                    setError('Loom video is not available or accessible');
                }
            } catch (error) {
                setError(
                    error instanceof Error
                        ? error.message
                        : 'Loom video validation failed'
                );
            } finally {
                setIsLoading(false);
            }
        };

        validateVideo();
    }, [loomUrl]);

    return {
        isLoading,
        error
    };
};
